import React, { useCallback, useEffect, useState } from 'react';
import {
  Coins,
  ArrowDownToLine,
  Minus,
  Database,
  RotateCcw,
  Sparkles,
  SlidersHorizontal,
  Wallet,
  TrendingDown,
  ShoppingBag,
  Plus,
  ExternalLink,
  RefreshCw,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import {
  getCreditLedger,
  formatLedgerEntry,
  summarizeCreditLedger,
  createMerchantPaymentLink,
  syncMerchantPaymentLinkStatus,
  listMerchantPaymentLinks,
} from '../../api/loan-action-creator';
import {
  getPaymentReturnUrl,
  rememberPendingPaymentLink,
} from '../payments/RazorpayReturn';

const TOPUP_PRESETS = [
  { amountRupees: 1000, label: '₹1,000' },
  { amountRupees: 2500, label: '₹2,500' },
  { amountRupees: 5000, label: '₹5,000' },
  { amountRupees: 10000, label: '₹10,000' },
];

const formatNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString('en-IN');
};

const formatSignedCredits = (credits, isCreditIn) => {
  const abs = formatNumber(Math.abs(credits));
  if (credits > 0 || isCreditIn) return `+${abs}`;
  if (credits < 0) return `-${abs}`;
  return abs;
};

const getLedgerIcon = (type) => {
  switch (type) {
    case 'topup':
      return <ArrowDownToLine size={18} />;
    case 'refund':
      return <RotateCcw size={18} />;
    case 'bonus':
      return <Sparkles size={18} />;
    case 'adjustment':
      return <SlidersHorizontal size={18} />;
    case 'deduction':
    default:
      return <Minus size={18} />;
  }
};

const getLedgerTone = (entry) => {
  if (entry.transactionType === 'refund' || entry.transactionType === 'bonus') return 'topup';
  if (entry.transactionType === 'adjustment') {
    return entry.credits >= 0 ? 'topup' : 'deduction';
  }
  return entry.isCreditIn ? 'topup' : 'deduction';
};

const makeClientRequestId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `idem-${crypto.randomUUID()}`;
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const isOpenUnpaid = (link) => {
  const status = (link?.status || '').toLowerCase();
  return status && !['paid', 'cancelled', 'expired', 'failed'].includes(status);
};

const isPaidLink = (link) => (link?.status || '').toLowerCase() === 'paid';

const formatLinkStatusLabel = (link) => {
  const status = (link?.status || 'created').toLowerCase();
  if (status === 'paid') {
    const credits = (link.creditsPosted || '').toLowerCase();
    if (credits === 'yes') return 'Paid · Credited';
    if (credits === 'processing') return 'Paid · Crediting…';
    if (credits === 'failed') return 'Paid · Credit failed';
    return 'Paid';
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const upsertPaymentLink = (list, link) => {
  if (!link?.paymentLinkId) return list;
  const idx = list.findIndex((l) => l.paymentLinkId === link.paymentLinkId);
  if (idx === -1) return [link, ...list];
  const next = [...list];
  next[idx] = { ...next[idx], ...link };
  return next;
};

const sortPaymentLinks = (links = []) =>
  [...links].sort((a, b) => {
    const aPaid = isPaidLink(a) ? 1 : 0;
    const bPaid = isPaidLink(b) ? 1 : 0;
    if (aPaid !== bPaid) return aPaid - bPaid; // unpaid first
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bTime - aTime;
  });

const CreditsLedger = ({ clinicCode }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({
    creditsLeft: 0,
    totalPurchased: 0,
    utilisedCredits: 0,
    entries: [],
  });
  const [showAll, setShowAll] = useState(false);

  const [selectedAmount, setSelectedAmount] = useState(TOPUP_PRESETS[1].amountRupees);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [topupError, setTopupError] = useState(null);
  const [topupMessage, setTopupMessage] = useState(null);
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [activeLinkId, setActiveLinkId] = useState(null);

  const merchantId = localStorage.getItem('doctorId') || '';
  const doctorCode = clinicCode || localStorage.getItem('merchantCode') || '';

  const loadLedger = useCallback(() => {
    setLoading(true);
    setError(null);
    setShowAll(false);

    const params = {};
    if (clinicCode) params.clinic = clinicCode;

    getCreditLedger(
      params,
      (data) => {
        const formatted = data.results.map(formatLedgerEntry);
        setSummary(summarizeCreditLedger(formatted));
        setLoading(false);
      },
      (err) => {
        const status = err?.response?.status;
        const message =
          status === 401 || status === 403
            ? 'Unauthorized. Check Lumi API credentials.'
            : err.message || 'Failed to fetch credit ledger';
        setError(message);
        setSummary({
          creditsLeft: 0,
          totalPurchased: 0,
          utilisedCredits: 0,
          entries: [],
        });
        setLoading(false);
      }
    );
  }, [clinicCode]);

  const loadPaymentLinks = useCallback(() => {
    if (!merchantId) return;
    listMerchantPaymentLinks(
      merchantId,
      (links) => {
        const creditLinks = sortPaymentLinks(
          links.filter((l) => (!l.purpose || l.purpose === 'CREDIT_TOPUP') && l.paymentLinkId)
        );
        setPaymentLinks(creditLinks);
        const open = creditLinks.find((l) => isOpenUnpaid(l));
        if (open) setActiveLinkId(open.paymentLinkId);
      },
      () => {
        /* non-blocking */
      }
    );
  }, [merchantId]);

  useEffect(() => {
    loadLedger();
    loadPaymentLinks();
  }, [loadLedger, loadPaymentLinks]);

  const resolvedAmount = useCustom
    ? Number(customAmount)
    : Number(selectedAmount);

  const activeLink =
    paymentLinks.find((l) => l.paymentLinkId === activeLinkId) ||
    paymentLinks.find((l) => isOpenUnpaid(l)) ||
    null;

  const linkPaid = Boolean(
    activeLink &&
      isPaidLink(activeLink) &&
      (activeLink.creditsPosted || '').toLowerCase() === 'yes'
  );

  const handleCreateLink = () => {
    setTopupError(null);
    setTopupMessage(null);

    if (!merchantId) {
      setTopupError('Doctor ID missing. Please log in again.');
      return;
    }
    if (!Number.isFinite(resolvedAmount) || resolvedAmount < 1) {
      setTopupError('Enter a valid amount (minimum ₹1).');
      return;
    }

    setCreatingLink(true);
    const payload = {
      merchantId,
      amountRupees: resolvedAmount,
      purpose: 'CREDIT_TOPUP',
      // referenceId,
      clientRequestId: makeClientRequestId(),
      description: `Credit top-up · ${doctorCode || merchantId}`,
      createdBy: 'merchant-dashboard',
      forceNew: false,
      callbackUrl: getPaymentReturnUrl(),
    };

    createMerchantPaymentLink(
      payload,
      (link) => {
        setCreatingLink(false);
        setPaymentLinks((prev) => sortPaymentLinks(upsertPaymentLink(prev, link)));
        setActiveLinkId(link.paymentLinkId);
        rememberPendingPaymentLink(link.paymentLinkId);
        setTopupMessage(
          link.reusedExisting
            ? 'Reusing an open unpaid payment link.'
            : 'Payment link created. Complete payment to load credits.'
        );
        if (link.paymentLinkUrl) {
          window.location.assign(link.paymentLinkUrl);
        }
      },
      (err) => {
        setCreatingLink(false);
        const apiMsg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;
        setTopupError(apiMsg || 'Failed to create payment link');
      }
    );
  };

  const handleSyncStatus = useCallback(
    (paymentLinkId) => {
      if (!paymentLinkId) return;

      setSyncingId(paymentLinkId);
      setTopupError(null);

      syncMerchantPaymentLinkStatus(
        paymentLinkId,
        (result) => {
          setSyncingId(null);
          setPaymentLinks((prev) => sortPaymentLinks(upsertPaymentLink(prev, result)));
          setActiveLinkId(paymentLinkId);

          const status = (result.status || '').toLowerCase();
          const creditsPosted = (result.creditsPosted || '').toLowerCase();

          if (status === 'paid' && creditsPosted === 'yes') {
            setTopupMessage('Payment confirmed. Credits added to your ledger.');
            loadLedger();
            return;
          }

          if (status === 'paid' && creditsPosted === 'processing') {
            setTopupMessage('Payment received. Crediting your ledger…');
            return;
          }

          if (status === 'paid' && creditsPosted === 'failed') {
            setTopupError('Payment received but credit top-up failed. Retry sync.');
            return;
          }

          setTopupMessage(
            status === 'paid'
              ? 'Payment received. Waiting for credits…'
              : `Payment status: ${status || 'pending'}. Pay on the link, then sync again.`
          );
        },
        (err) => {
          setSyncingId(null);
          const apiMsg =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message;
          setTopupError(apiMsg || 'Failed to sync payment status');
        }
      );
    },
    [loadLedger]
  );

  const syncPaymentLink = (paymentLinkId) => {
    const id = paymentLinkId || activeLinkId || activeLink?.paymentLinkId;
    if (!id) return;
    setActiveLinkId(id);
    handleSyncStatus(id);
  };

  const visibleEntries = showAll ? summary.entries : summary.entries.slice(0, 6);
  const usagePct =
    summary.totalPurchased > 0
      ? Math.min(100, Math.round((summary.utilisedCredits / summary.totalPurchased) * 100))
      : 0;

  if (loading) {
    return (
      <div className="credits-page" aria-busy="true" aria-label="Loading credits">
        <section className="credits-overview-card credits-skeleton-card">
          <div className="credits-overview-head">
            <div className="pl-skeleton-block">
              <span className="skeleton skeleton-line lg" />
              <span className="skeleton skeleton-line md" />
            </div>
          </div>
          <div className="credits-overview-hero">
            <span className="skeleton skeleton-circle lg" />
            <span className="skeleton skeleton-line xl" />
          </div>
          <span className="skeleton skeleton-bar" />
          <div className="credits-mini-stats">
            {[1, 2, 3].map((i) => (
              <div key={i} className="credits-mini-stat">
                <span className="skeleton skeleton-square" />
                <div className="pl-skeleton-block">
                  <span className="skeleton skeleton-line xs" />
                  <span className="skeleton skeleton-line sm" />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="credits-ledger-card credits-skeleton-card">
          <div className="credits-ledger-head">
            <div className="pl-skeleton-block">
              <span className="skeleton skeleton-line lg" />
              <span className="skeleton skeleton-line md" />
            </div>
            <span className="skeleton skeleton-pill view-all" />
          </div>
          <ul className="credits-ledger-list">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <li key={i} className="credits-ledger-item credits-skeleton-item">
                <span className="skeleton skeleton-circle md" />
                <div className="credits-ledger-main">
                  <div className="credits-ledger-top">
                    <span className="skeleton skeleton-line md" />
                    <span className="skeleton skeleton-line sm" />
                  </div>
                  <div className="credits-ledger-bottom">
                    <span className="skeleton skeleton-line lg" />
                    <span className="skeleton skeleton-line xs" />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="credits-page">
        <div className="credits-error-card">
          <h3>Unable to load credits</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="credits-page">
      <section className="credits-overview-card">
        <header className="credits-overview-head">
          <h2>Credits Overview</h2>
          <p>Your current credit summary{clinicCode ? ` · ${clinicCode}` : ''}</p>
        </header>

        <div className="credits-overview-hero">
          <div className="credits-coin-stack" aria-hidden="true">
            <span className="credits-coin coin-back" />
            <span className="credits-coin coin-mid" />
            <span className="credits-coin coin-front">
              <Coins size={22} />
            </span>
          </div>

          <div className="credits-left-block">
            <span className="credits-left-label">Credits Left</span>
            <span className="credits-left-value">{formatNumber(summary.creditsLeft)}</span>
          </div>
        </div>

        <div className="credits-usage-bar" aria-hidden="true">
          <div className="credits-usage-fill" style={{ width: `${usagePct}%` }} />
        </div>

        <div className="credits-mini-stats">
          <div className="credits-mini-stat">
            <div className="credits-mini-icon utilised">
              <TrendingDown size={16} />
            </div>
            <div>
              <span className="credits-mini-label">Utilised</span>
              <span className="credits-mini-value">{formatNumber(summary.utilisedCredits)}</span>
            </div>
          </div>
          <div className="credits-mini-stat">
            <div className="credits-mini-icon purchased">
              <ShoppingBag size={16} />
            </div>
            <div>
              <span className="credits-mini-label">Purchased</span>
              <span className="credits-mini-value">{formatNumber(summary.totalPurchased)}</span>
            </div>
          </div>
          <div className="credits-mini-stat">
            <div className="credits-mini-icon wallet">
              <Wallet size={16} />
            </div>
            <div>
              <span className="credits-mini-label">Available</span>
              <span className="credits-mini-value">{formatNumber(summary.creditsLeft)}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="credits-topup-card">
        <header className="credits-topup-head">
          <div>
            <h2>Load Credits</h2>
            <p>Pay via Razorpay — credits post to your ledger after confirmation</p>
          </div>
        </header>

        <div className="credits-topup-body">
          <div className="credits-amount-grid" role="group" aria-label="Top-up amount">
            {TOPUP_PRESETS.map((preset) => (
              <button
                key={preset.amountRupees}
                type="button"
                className={`credits-amount-chip ${
                  !useCustom && selectedAmount === preset.amountRupees ? 'active' : ''
                }`}
                onClick={() => {
                  setUseCustom(false);
                  setSelectedAmount(preset.amountRupees);
                }}
              >
                {preset.label}
              </button>
            ))}
            <button
              type="button"
              className={`credits-amount-chip ${useCustom ? 'active' : ''}`}
              onClick={() => setUseCustom(true)}
            >
              Custom
            </button>
          </div>

          {useCustom && (
            <div className="credits-custom-amount">
              <span className="credits-rupee">₹</span>
              <input
                type="number"
                min="1"
                step="1"
                className="credits-custom-input"
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
              />
            </div>
          )}

          <div className="credits-topup-actions">
            <button
              type="button"
              className="credits-pay-btn"
              onClick={handleCreateLink}
              disabled={creatingLink}
            >
              {creatingLink ? (
                <>
                  <Loader2 size={16} className="credits-spin" />
                  Creating link…
                </>
              ) : (
                <>
                  <Plus size={16} />
                  Pay ₹{formatNumber(resolvedAmount || 0)}
                </>
              )}
            </button>
          </div>

          {activeLink && isOpenUnpaid(activeLink) && (
            <div className="credits-pending-box">
              <div className="credits-pending-row">
                <div>
                  <strong>Payment pending</strong>
                  <p>
                    {activeLink.amountRupees != null
                      ? `₹${formatNumber(activeLink.amountRupees)} · `
                      : ''}
                    Status: {(activeLink.status || 'created').toLowerCase()}
                    {activeLink.creditsPosted
                      ? ` · Credits: ${activeLink.creditsPosted}`
                      : ''}
                  </p>
                </div>
                <div className="credits-pending-actions">
                  {activeLink.paymentLinkUrl && (
                    <a
                      className="credits-link-btn"
                      href={activeLink.paymentLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink size={14} />
                      Open link
                    </a>
                  )}
                  <button
                    type="button"
                    className="credits-sync-btn"
                    onClick={() => syncPaymentLink(activeLink.paymentLinkId)}
                    disabled={syncingId === activeLink.paymentLinkId}
                  >
                    {syncingId === activeLink.paymentLinkId ? (
                      <Loader2 size={14} className="credits-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    {syncingId === activeLink.paymentLinkId ? 'Checking…' : "I've paid"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {linkPaid && (
            <div className="credits-success-box">
              <CheckCircle2 size={18} />
              <span>Credits loaded successfully</span>
              <button
                type="button"
                className="credits-dismiss-btn"
                onClick={() => {
                  setActiveLinkId(null);
                  setTopupMessage(null);
                }}
              >
                Dismiss
              </button>
            </div>
          )}

          {paymentLinks.length > 0 && (
            <div className="credits-links-section">
              <div className="credits-links-head">
                <h3>Payment links</h3>
                <button
                  type="button"
                  className="credits-links-refresh"
                  onClick={loadPaymentLinks}
                >
                  <RefreshCw size={13} />
                  Refresh
                </button>
              </div>
              <ul className="credits-links-list">
                {paymentLinks.map((link) => {
                  const paid = isPaidLink(link);
                  const open = isOpenUnpaid(link);
                  const rowSyncing = syncingId === link.paymentLinkId;
                  return (
                    <li
                      key={link.paymentLinkId}
                      className={`credits-link-row ${paid ? 'paid' : open ? 'open' : ''}`}
                    >
                      <div className="credits-link-main">
                        <div className="credits-link-top">
                          <span className="credits-link-amount">
                            {link.amountRupees != null
                              ? `₹${formatNumber(link.amountRupees)}`
                              : '—'}
                          </span>
                          <span className={`credits-link-status ${paid ? 'paid' : open ? 'open' : ''}`}>
                            {formatLinkStatusLabel(link)}
                          </span>
                        </div>
                        <div className="credits-link-meta">
                          <span title={link.paymentLinkId}>{link.paymentLinkId}</span>
                          {link.clinicCode ? <span>· {link.clinicCode}</span> : null}
                        </div>
                      </div>
                      <div className="credits-link-actions">
                        {open && link.paymentLinkUrl && (
                          <a
                            className="credits-link-btn"
                            href={link.paymentLinkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink size={13} />
                            Open
                          </a>
                        )}
                        {(open ||
                          (paid &&
                            ['no', 'processing', 'failed', ''].includes(
                              (link.creditsPosted || '').toLowerCase()
                            ))) && (
                          <button
                            type="button"
                            className="credits-sync-btn"
                            onClick={() => syncPaymentLink(link.paymentLinkId)}
                            disabled={rowSyncing}
                          >
                            {rowSyncing ? (
                              <Loader2 size={13} className="credits-spin" />
                            ) : (
                              <RefreshCw size={13} />
                            )}
                            {rowSyncing ? '…' : paid ? 'Sync' : "I've paid"}
                          </button>
                        )}
                        {paid && (link.creditsPosted || '').toLowerCase() === 'yes' && (
                          <span className="credits-link-credited">
                            <CheckCircle2 size={14} />
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {topupMessage && !linkPaid && (
            <p className="credits-topup-msg">{topupMessage}</p>
          )}
          {topupError && <p className="credits-topup-error">{topupError}</p>}
        </div>
      </section>

      <section className="credits-ledger-card">
        <header className="credits-ledger-head">
          <div>
            <h2>Credits Ledger</h2>
            <p>Recent credit transactions</p>
          </div>
          {summary.entries.length > 6 && (
            <button
              type="button"
              className="credits-view-all"
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll ? 'Show Less' : 'View All'}
            </button>
          )}
        </header>

        {visibleEntries.length === 0 ? (
          <div className="credits-empty">
            <div className="credits-empty-icon">
              <Database size={24} />
            </div>
            <h3>No ledger entries</h3>
            <p>Credit transactions will appear here</p>
          </div>
        ) : (
          <ul className="credits-ledger-list">
            {visibleEntries.map((entry) => {
              const tone = getLedgerTone(entry);
              return (
                <li key={entry.id} className="credits-ledger-item">
                  <div className={`credits-ledger-icon ${tone}`}>
                    {getLedgerIcon(entry.transactionType)}
                  </div>

                  <div className="credits-ledger-main">
                    <div className="credits-ledger-top">
                      <span className="credits-ledger-title">{entry.title}</span>
                      <span className={`credits-ledger-amount ${tone}`}>
                        {formatSignedCredits(entry.credits, entry.isCreditIn)}
                      </span>
                    </div>
                    <div className="credits-ledger-bottom">
                      <span className="credits-ledger-detail" title={entry.description}>
                        {entry.detail}
                      </span>
                      <span className="credits-ledger-date">
                        {entry.dateLabel}
                        {entry.timeLabel ? `, ${entry.timeLabel}` : ''}
                      </span>
                    </div>
                    {entry.balanceAfter != null && (
                      <span className="credits-balance-chip">
                        Balance {formatNumber(entry.balanceAfter)}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <p className="credits-ledger-note">All transactions are in credits</p>
      </section>
    </div>
  );
};

export default CreditsLedger;
